import { CSSProperties, useState } from "react";
import Colors from "modules/Colors";
import Sty from "modules/Sty";
import ButtonSymphony, { ButtonSize, ButtonVariant } from "elements/ButtonSymphony";
import Icon from "elements/Icon";
import Loader from "elements/Loader";
import { clsx, makeKey, styx } from "modules/Utils";
import { AssetPlatformType } from "components/CampaignViews/AdCreativeSelectorCampaignView";
import { CreativeSelectorPost } from "Hooks/useCampaignBuilder";
import millify from "millify";
import ModalSymphony from "./ModalSymphony";
import DebugBox from "components/shareable/DebugBox";
import AdPreview from "./AdPreview";
import { MediaType } from "modules/Types";
import dayjs from "dayjs";
import useScreen from 'Hooks/useScreen';


export interface ItemSelectorAction {
    title: string;
    titleSelected?: string;
    buttonVariant: ButtonVariant;
    buttonVariantSelected?: ButtonVariant;
    buttonSize: ButtonSize;
    onClick: (item: any) => void;
    hideDefaultState?: boolean;
    hideSelectedState?: boolean;
}

/** Props for the ItemSelector component that displays a horizontal scrollable list of selectable items */
interface ItemSelectorProps {
    /** CSS class name for styling the root component */
    className: string;
    /** Array of items to display in the selector. Each item should conform to CreativeSelectorPost interface */
    items: any[];
    /** Array of currently selected items */
    selectedItems: any[];
    /** Whether the items are currently being loaded */
    isLoading: boolean;
    /** Callback function triggered when an item is selected/deselected 
     * @param item - The item that was clicked
     */
    onSelectItem: (item: any) => void;
    /** Label for the button */
    actions?: ItemSelectorAction[];
    /** Maximum number of items that can be selected */
    maxSelectedItems?: number;
    /** Whether to show the text overlay on the post */
    showTextOverlay?: boolean;
    /** Whether to apply Maestro AI styling to the items */
    isMaestroStyle?: boolean;
    /** Whether to show the View button (defaults to true) */
    showViewButton?: boolean;
    /** Layout direction - horizontal scrolling or vertical grid */
    layout?: 'horizontal' | 'vertical';
    /** Optional callback function to load more items when the user clicks the "Load more" button 
     * If provided, a "Load more" button will be displayed at the end of the list
     */
    onLoadMore?: () => void;
    /** Whether additional items are currently being loaded (for the load more button) */
    isLoadingMore?: boolean;
}

const ItemSelector = ({
    className,
    items,
    selectedItems,
    isLoading,
    onSelectItem,
    actions = [],
    maxSelectedItems = 1,
    showTextOverlay = false,
    isMaestroStyle = false,
    showViewButton = true,
    layout = 'horizontal',
    onLoadMore,
    isLoadingMore = false
}: ItemSelectorProps) => {
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedPostForPreview, setSelectedPostForPreview] = useState<CreativeSelectorPost | null>(null);
    const { mobileView, mStyle } = useScreen();

    const getPostTitle = (post: CreativeSelectorPost) => {
        if (post.backgroundColor) {
            return null;
        }

        if (showTextOverlay) {
            return post.title;
        }

        return null
    }

    const recommendedByMaestro = () => {
        return (
            <div className="recommendedByMaestroContainer">
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
                    <Icon
                        className="recommendedByMaestroIcon"
                        icon="animationAI"
                        size={20}
                    />
                    <div className="recommendedByMaestroText" style={Sty.text12}>
                        Recommended by Maestro
                    </div>
                </div>
                <ButtonSymphony
                    className="learnWhyButton"
                    style={sty.learnWhyButton}
                    onClick={() => { }}
                    variant="text"
                    size="small"
                >
                    Learn why
                </ButtonSymphony>
            </div>
        )
    }

    const renderPostType = (post: CreativeSelectorPost) => {
        if (!post) {
            return null;
        }

        let isRecommendedByMaestro = post.isRecommended;
        const isDisabled = post.disabled === true;

        const renderStatItems = () => {
            let statItems = [];
            if (post.likesCount) {
                statItems.push({
                    icon: 'heart',
                    value: millify(post.likesCount)
                })
            }
            if (post.commentsCount) {
                statItems.push({
                    icon: 'comments',
                    value: millify(post.commentsCount)
                })
            }


            if (statItems.length === 0) {
                return null;
            }

            return (
                <div
                    className="postVisualIconContainer"
                    style={sty.postVisualIconContainer}
                >
                    {MediaType.VIDEO === post.mediaType &&
                        <Icon
                            className="postVisualIcon1"
                            icon="video3"
                            color="white"
                            size={20}
                            style={{
                                marginRight: 'auto',
                                marginLeft: 8,
                                zIndex: 1
                            }}
                        />
                    }
                    {statItems.map((item, index) => (
                        <div
                            key={makeKey(item.icon, index)}
                            className="postVisualIconContainerItem"
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 4,
                                zIndex: 1
                            }}
                        >
                            <Icon
                                className="postVisualIcon2"
                                icon={item.icon}
                                size={16}
                                color="white"
                            />
                            <div style={Sty.text12white}>
                                {item.value}
                            </div>
                        </div>
                    ))}
                </div>
            )
        }

        /** Renders the post content for maestro style when enabled and not disabled */
        const renderMaestroContent = () => {
            return (
                <>
                    <div className="postVisualMaestroIndicator" style={sty.postVisualMaestroIndicator}>
                        <Icon
                            className="maestroRecommendationIcon"
                            icon={"maestroBadgeImage"}
                            size={24}
                        />
                    </div>
                    <div className="postVisualTitleCentered" style={styx(sty.postVisualTitleCentered)}>
                        {post.title}
                    </div>
                </>
            );
        }

        /** Renders the post content for default style */
        const renderDefaultContent = () => {
            if (post.backgroundColor) {
                return (
                    <div className="postVisualTitleCentered" style={sty.postVisualTitleCentered}>
                        {post.title}
                    </div>
                );
            } else {
                return (
                    <div className="postVisualOverlay" style={sty.postVisualOverlay} />
                );
            }
        }

        /** Renders the post content for disabled maestro style */
        const renderDisabledMaestroContent = () => {
            return (
                <>
                    <div className="postVisualMaestroIndicator" style={sty.postVisualMaestroIndicator}>
                        <Icon
                            className="maestroRecommendationIcon"
                            icon={"closeCircle"}
                            size={24}
                            color={Colors.greyDark}
                        />
                    </div>
                    <div className="postVisualTitleCentered" style={styx(
                        sty.postVisualTitleCentered,
                        sty.disabledText
                    )}>
                        {post.title}
                    </div>
                </>
            );
        }

        /** Renders the inner content of the post visual based on styling and disabled state */
        const renderPostContent = () => {
            if (isMaestroStyle) {
                if (isDisabled) {
                    return renderDisabledMaestroContent();
                } else {
                    return renderMaestroContent();
                }
            } else {
                const content = renderDefaultContent();
                return (
                    <>
                        {content}
                        {renderStatItems()}
                    </>
                );
            }
        }

        switch (post.originalPostType) {
            case AssetPlatformType.INSTAGRAM:
            case AssetPlatformType.CLOUDINARY:
            case AssetPlatformType.TIKTOK:
            case AssetPlatformType.IMPORTED_FROM_URL:
                let thumbnail = post.thumbnailUrl;
                if (!thumbnail && post.mediaType === MediaType.IMAGE) {
                    thumbnail = post.assetUrl;
                }

                // Apply maestro styling if enabled
                const backgroundStyle = isMaestroStyle ?
                    {
                        background: isDisabled
                            ? `linear-gradient(45deg, ${Colors.greyLight}, ${Colors.greyLighter})`
                            : `linear-gradient(45deg, ${Colors.purpleLighter}, ${Colors.purpleLightest})`,
                        border: `1px solid ${isDisabled ? Colors.greyLight : Colors.purpleLight}`,
                        position: 'relative' as const,
                    } :
                    {
                        backgroundImage: `url(${thumbnail})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundColor: post.backgroundColor,
                    };

                return (
                    <div
                        className="postVisual"
                        style={styx(
                            sty.postVisual,
                            backgroundStyle,
                            isDisabled && sty.disabledPostVisual
                        )}
                    >
                        {renderPostContent()}
                    </div>
                )
            default:
                return (
                    <div
                        className="postVisual"
                        style={styx(
                            sty.postVisual,
                            isDisabled && sty.disabledPostVisual
                        )}
                    >
                        {renderPostContent()}
                    </div>
                )
        }
    }

    /** Gets the selected index of a post based on its type and ID */
    const getSelectedPostIndex = (post: CreativeSelectorPost): number => {
        switch (post.originalPostType) {
            case AssetPlatformType.INSTAGRAM: {
                const index = selectedItems.findIndex(p => p.id === post.id);
                return index === -1 ? -1 : index + 1;
            }
            case AssetPlatformType.CLOUDINARY: {
                const index = selectedItems.findIndex(p => p.id === post.id);
                return index === -1 ? -1 : index + 1;
            }
            case AssetPlatformType.TIKTOK: {
                const index = selectedItems.findIndex(p => p.item_info?.item_id === post.id);
                return index === -1 ? -1 : index + 1;
            }
            case AssetPlatformType.IMPORTED_FROM_URL: {
                const index = selectedItems.findIndex(p => p.id === post.id);
                return index === -1 ? -1 : index + 1;
            }
            default:
                return -1;
        }
    }

    const renderPostSelectedNumber = (post: CreativeSelectorPost) => {
        if (!post) {
            return null;
        }

        // const selectedIndex = getSelectedPostIndex(post);
        // if (selectedIndex === -1) {
        //     return null
        // }

        return (
            <div
                className="postSelectedIndicator"
                style={sty.postSelectedIndicator}
            >
                <Icon
                    icon="heart"
                    size={16}
                    color="white"
                    className="checkmarkIcon"
                />
            </div>
        )
    }

    const renderActions = (post: CreativeSelectorPost) => {
        if (!post || !actions || actions.length === 0) {
            return null
        };

        const isDisabled = post.disabled === true;

        return actions.map((action, index) => {
            if (!action) {
                return null;
            }

            const isSelected = selectedItems && selectedItems.some(p => p && p.id === post.id);

            let buttonVariant = action.buttonVariant;
            let buttonTitle = action.title;

            if (!isSelected && action.hideDefaultState) {
                return null;
            } else if (isSelected && action.hideSelectedState) {
                return null;
            }

            if (isSelected && action.buttonVariantSelected) {
                buttonVariant = action.buttonVariantSelected;
                buttonTitle = action.titleSelected || action.title;
            }

            return (
                <div
                    key={makeKey('action', action.title || 'action', index)}
                    className="itemSelectorActionButtonsContainer"
                    style={sty.itemSelectorActionButtonsContainer}
                >
                    {showViewButton && (
                        <ButtonSymphony
                            className="itemSelectorActionButton"
                            variant={buttonVariant}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (post) {
                                    setSelectedPostForPreview(post);
                                    setShowPreviewModal(true);
                                }
                            }}
                            size={mobileView ? 'small' : action.buttonSize}
                            width="100%"
                        >
                            View
                        </ButtonSymphony>
                    )}
                    <ButtonSymphony
                        className="itemSelectorActionButton"
                        variant={buttonVariant}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (post && action.onClick && !isDisabled) {
                                action.onClick(post);
                            }
                        }}
                        size={mobileView ? 'small' : action.buttonSize}
                        width="100%"
                        disabled={isDisabled}
                    >
                        {buttonTitle || 'Action'}
                    </ButtonSymphony>
                </div>
            )
        }).filter(Boolean) // Filter out any nulls from the map
    }

    const renderLoadMoreButton = () => {
        if (!onLoadMore) {
            return null;
        }

        return (
            <div
                className="loadMoreButtonContainer"
                style={layout === 'vertical' ? sty.verticalLoadMoreContainer : sty.loadMoreButtonContainer}
            >
                <ButtonSymphony
                    className="loadMoreItemsButton"
                    onClick={onLoadMore}
                    variant="outlined"
                    size="small"
                    disabled={isLoadingMore}
                    style={sty.loadMoreItemsButton}
                >
                    {isLoadingMore ?
                        <div style={sty.loadMoreButtonLoading}>
                            <Loader size={16} />
                            <span>Loading...</span>
                        </div>
                        :
                        'Load more'
                    }
                </ButtonSymphony>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="postsLoadingContainer" style={sty.postsLoadingContainer}>
                <Loader title="Loading posts..." size={24} />
            </div>
        )
    }

    if (!items.length) {
        return (
            <div className="noPostsContainer" style={sty.noPostsContainer}>
                <div style={Sty.text16grey}>
                    No posts found
                </div>
            </div>
        )
    }

    return (
        <>
            <div
                className={clsx('itemSelectorElement', className)}
                style={layout === 'vertical' ? mStyle(sty, 'itemSelectorElementVertical') : sty.itemSelectorElement}
            >
                {items && items.filter(item => {
                    return item && item.id
                }).map((post, index) => {
                    if (!post || !post.id) {
                        return null;
                    }

                    const isSelected = selectedItems && selectedItems.some(p => p && p.id === post.id);
                    const isDisabled = post.disabled === true;

                    return (
                        <div
                            key={makeKey('post-container', post.id || `item-${index}`, index)}
                            className="postContainer"
                            style={styx(
                                sty.postContainer,
                                isSelected ? sty.selectedPostContainer : null,
                                isMaestroStyle ? sty.maestroPostContainer : null,
                                isSelected && isMaestroStyle ? sty.selectedMaestroPostContainer : null,
                                isDisabled ? sty.disabledPostContainer : null
                            )}
                            onClick={() => {
                                if (post && onSelectItem && !isDisabled) {
                                    onSelectItem(post);
                                }
                            }}
                        >
                            {isSelected && renderPostSelectedNumber(post)}
                            {renderPostType(post)}
                            {getPostTitle(post)}
                            {actions?.length > 0 && renderActions(post)}
                            {isDisabled && isMaestroStyle && (
                                <div style={sty.disabledPostDescription}>
                                    {post.description}
                                </div>
                            )}
                        </div>
                    )
                })}
                {layout === 'horizontal' && renderLoadMoreButton()}
                {layout === 'vertical' && renderLoadMoreButton()}
            </div>
            <ModalSymphony
                className="itemSelectorPreviewModal"
                isOpen={showPreviewModal}
                onClose={() => setShowPreviewModal(false)}
                title={selectedPostForPreview?.title || ''}
                maxWidth={800}
            >
                <div className="modalPreviewContainer" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                    padding: 16,
                    width: '100%'
                }}>
                    {selectedPostForPreview && (
                        <>
                            <AdPreview
                                className="modalAdPreview"
                                style={{ width: '100%', maxHeight: 600 }}
                                previewedAsset={selectedPostForPreview}
                                username={selectedPostForPreview.platformCaption?.split('\n')[0] || 'User'}
                                callToActionLabel="Learn More"
                                profilePicUrl={null}
                                captions={[]}
                                previewType="ig-feed"
                            />
                            {selectedPostForPreview.mediaType === MediaType.CAROUSEL_ALBUM && selectedPostForPreview.children && selectedPostForPreview.children.length > 1 && (
                                <div className="carouselInfoText" style={{
                                    fontSize: 14,
                                    color: Colors.greyDark,
                                    textAlign: 'center'
                                }}>
                                    This post contains multiple images. Use the carousel controls to navigate between them.
                                </div>
                            )}
                            {selectedPostForPreview.platformCaption && (
                                <div className="postCaption" style={{
                                    fontSize: 14,
                                    color: Colors.greyDark,
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.5
                                }}>
                                    {selectedPostForPreview.platformCaption}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    itemSelectorElement: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        paddingTop: 16,
        overflowX: 'auto',
        padding: 8,
        boxShadow: Sty.boxShadowInset,
        borderRadius: 8,
        width: '100%',
    },
    postContainer: {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        gap: 8,
        border: Sty.border,
        borderRadius: 8,
        padding: 8,
        cursor: 'pointer',
        flexShrink: 0,
        backgroundColor: 'white',
    },
    selectedPostContainer: {
        border: `1px solid ${Colors.purple}`,
        backgroundColor: Colors.purpleLightest
    },
    postVisual: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        objectFit: 'cover',
        borderRadius: 8,
        overflow: 'hidden',
        width: 160,
        height: 160,
    },
    postsLoadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
        width: '100%',
    },
    noPostsContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
        width: '100%'
    },
    postSelectedIndicator: {
        position: 'absolute',
        top: 4,
        right: 4,
        backgroundColor: Colors.purple,
        color: Colors.white,
        borderRadius: 999,
        width: 24,
        height: 24,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1,
    },
    learnWhyButton: {
        marginTop: 8,
        marginLeft: 'auto',
        marginRight: 8
    },
    postVisualIconContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 12,
        paddingRight: 8,
        paddingBottom: 4,
        width: '100%',
    },

    itemSelectorActionButtonsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    postVisualOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: 60,
        background: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))'
    },

    itemPreviewContainer: {
        backgroundColor: Colors.greyLight,
        padding: 16,
        boxShadow: Sty.boxShadow,
    },
    itemPreviewContainerInner: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: 16,
        overflow: 'hidden',
    },
    itemPreviewInfoContainer: {
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        gap: 8,
    },
    itemPreviewDate: {
        ...Sty.text14grey,
        marginLeft: 'auto',
    },
    postVisualTitleCentered: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        textAlign: 'center',
        padding: 8,
        fontWeight: 500,
        color: Colors.black,
    },
    maestroPostContainer: {
        border: `1px solid ${Colors.purpleLight}`,
        boxShadow: `0 4px 8px rgba(138, 43, 226, 0.1)`,
    },
    selectedMaestroPostContainer: {
        border: `2px solid ${Colors.purple}`,
        backgroundColor: Colors.purpleLightest,
    },
    postVisualMaestroIndicator: {
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 1,
    },
    loadMoreButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 80,
        height: 80,
        flexShrink: 0,
        padding: 8,
    },
    loadMoreItemsButton: {
        width: '100%',
        height: '100%',
    },
    loadMoreButtonLoading: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    disabledPostContainer: {
        cursor: 'default',
        opacity: 1,
        border: `1px solid ${Colors.greyLight}`,
        backgroundColor: Colors.greyLight || '#f5f5f5',
    },
    disabledPostVisual: {
        opacity: 0.6,
    },
    disabledText: {
        color: Colors.greyDark,
    },
    disabledPostDescription: {
        color: Colors.greyDark,
        fontSize: 12,
        marginTop: 4,
        textAlign: 'center',
    },
    itemSelectorElementVertical: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 16,
        padding: 16,
        overflowY: 'auto',
        height: '100%',
        width: '100%',
        boxShadow: Sty.boxShadowInset,
        borderRadius: 8,
    },
    itemSelectorElementVertical_mobile: {
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 12,
        padding: '12px 16px',
    },
    verticalLoadMoreContainer: {
        gridColumn: '1 / -1',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        padding: 16,
        marginTop: 8,
    },
}

export default ItemSelector;